import { selectLocalNotificationBudget } from './localNotificationBudget'
import { getLocalNotificationsModule, scheduleLocalNotifications } from './localNotificationsPlugin'
import { notificationFireTime, notificationOwnerKey, scopeKey } from './notificationScope'

export const SU_GUIDE_LOCAL_NOTIFICATION_KINDS = new Set(['first_class', 'tomorrow_preview', 'next_class', 'test_class', 'personal_event'])
const ownerFor = n => n?.extra?.debug || n?.extra?.kind === 'test_class' ? 'debug' : n?.extra?.kind === 'personal_event' ? 'personal' : 'course'
function owned(n) {
 if (!SU_GUIDE_LOCAL_NOTIFICATION_KINDS.has(n?.extra?.kind)) return false
 if (n.extra.owner === 'suguide') return true
 // Only legacy IDs produced by this app, with its routing metadata, may be retired.
 const id = Number(n.id), kind = n.extra.kind
 const range = kind === 'next_class' ? [100000000,400000000] : kind === 'personal_event' ? [400000000,700000000] : kind === 'first_class' ? [700000000,1000000000] : kind === 'tomorrow_preview' ? [1200000000,1500000000] : [1000000000,1100000000]
 return id >= range[0] && id < range[1] && n.extra.deepLink === '/timetable'
}
const at = notificationFireTime
const signature = n => JSON.stringify([Number(n.id), n.title, n.body ?? '', at(n), n.extra?.logicalKey ?? null, n.extra?.contentKey ?? null])

export function createLocalNotificationCoordinator({ getModule = getLocalNotificationsModule, getLocalPlugin = null } = {}) {
 let generation = 0, activeScope, pendingOperation = Promise.resolve()
 const candidatesByOwner = new Map()
 const initialized = new Set()
 // Always resolve a plain container; Capacitor plugin proxies are thenable traps.
 const getPluginModule = async () => typeof getLocalPlugin === 'function' ? { LocalNotifications: await getLocalPlugin() } : getModule()
 function enqueue(operation) {
  const result = pendingOperation.then(operation)
  pendingOperation = result.catch(() => {})
  return result
 }
 function activate(scope) {
  if (scopeKey(activeScope) === scopeKey(scope)) return
  activeScope = scope
  generation++
  candidatesByOwner.clear()
  initialized.clear()
 }
 async function reconcile(requestGeneration) {
  const plugin = (await getPluginModule())?.LocalNotifications
  if (!plugin) return { count: 0, skipped: 'not_native' }
  if (requestGeneration !== generation) return { count: 0, skipped: 'superseded' }
  const pending = (await plugin.getPending())?.notifications ?? []
  if (requestGeneration !== generation) return { count: 0, skipped: 'superseded' }
  const currentOwner = activeScope ? notificationOwnerKey(activeScope) : null
  const managed = pending.filter(owned)
  const belongsToScope = n => !currentOwner || !n.extra.ownerKey || n.extra.ownerKey === currentOwner
  const notHydrated = managed.filter(n => !initialized.has(ownerFor(n)) && belongsToScope(n))
  const desired = [...candidatesByOwner.values()].flat().filter(n => at(n) > Date.now())
  const byId = new Map()
  for (const n of [...notHydrated, ...desired]) {
   if (!Number.isInteger(Number(n.id)) || Number(n.id) <= 0 || Number(n.id) > 2147483647) throw Error('invalid_notification_id')
   const previous = byId.get(Number(n.id))
   if (previous && previous.extra?.logicalKey !== n.extra?.logicalKey) throw Error('notification_id_collision')
   byId.set(Number(n.id), n)
  }
  const selected = selectLocalNotificationBudget([...byId.values()], Math.max(0, 60 - pending.filter(n => !owned(n)).length))
  const targets = new Map(selected.map(n => [Number(n.id), n]))
  // Unknown requests, even with the same ID, are never overwritten.
  if (pending.some(n => !owned(n) && targets.has(Number(n.id)))) throw Error('notification_id_not_owned')
  if (managed.some(n => belongsToScope(n) && targets.has(Number(n.id)) && n.extra?.logicalKey && targets.get(Number(n.id)).extra?.logicalKey && n.extra.logicalKey !== targets.get(Number(n.id)).extra.logicalKey)) throw Error('notification_id_collision')
  const obsolete = managed.filter(n => !belongsToScope(n) || !targets.has(Number(n.id)))
  if (obsolete.length) {
   await plugin.cancel({ notifications: obsolete.map(n => ({ id: Number(n.id) })) })
   const remaining = (await plugin.getPending())?.notifications ?? []
   if (remaining.some(n => obsolete.some(old => Number(old.id) === Number(n.id)))) throw Error('notification_cancel_unconfirmed')
  }
  if (requestGeneration !== generation) return { count: 0, skipped: 'superseded' }
  // Awaiting getPending/cancel may cross the fire time. Capacitor iOS treats
  // an expired schedule.at as immediate delivery; never replay that occurrence.
  const stillFuture = selected.filter(n => at(n) > Date.now())
  let additions = stillFuture.filter(n => !managed.some(p => belongsToScope(p) && signature(p) === signature(n)))
  let delivered = []
  if (additions.length && typeof plugin.getDeliveredNotifications === 'function') {
   // A delivered request is not a missing pending request. In particular, a
   // backwards clock adjustment must not replay an already delivered occurrence.
   delivered = (await plugin.getDeliveredNotifications())?.notifications ?? []
   additions = additions.filter(n => !delivered.some(d => n.extra?.logicalKey ? d.extra?.logicalKey === n.extra.logicalKey : Number(d.id) === Number(n.id)))
  }
  if (requestGeneration !== generation) return { count: 0, skipped: 'superseded' }
  additions = additions.filter(n => at(n) > Date.now())
  let scheduleError
  if (additions.length) {
   try { await scheduleLocalNotifications(plugin, additions) } catch (error) { scheduleError = error }
  }
  const after = (await plugin.getPending())?.notifications ?? []
  const expected = stillFuture.filter(n => !delivered.some(d => n.extra?.logicalKey ? d.extra?.logicalKey === n.extra.logicalKey : Number(d.id) === Number(n.id)))
  const missingIds = expected.filter(n => at(n) > Date.now() && !after.some(p => signature(p) === signature(n))).map(n => Number(n.id))
  if (scheduleError && missingIds.length) throw scheduleError
  return { count: expected.filter(n => at(n) > Date.now()).length, success: missingIds.length === 0, missingIds,
   scheduledKinds: [...new Set(additions.map(n => n.extra.kind))], generation: requestGeneration }
 }
 return {
  // Called synchronously on account changes, before asynchronous producers finish.
  setScope(scope) { activate(scope); const ticket = generation; return enqueue(() => reconcile(ticket)) },
  request(owner, candidates, scope = activeScope) {
   if (!['course','personal','debug'].includes(owner)) return Promise.reject(Error('invalid_notification_owner'))
   if (activeScope && scopeKey(scope) !== scopeKey(activeScope)) return Promise.resolve({ count: 0, skipped: 'stale_scope' })
   if (!activeScope && scope) activate(scope)
   candidatesByOwner.set(owner, [...candidates])
   initialized.add(owner)
   const ticket = generation
   return enqueue(() => reconcile(ticket))
  },
  cancelKinds(kinds) {
   const cancelled = new Set(kinds)
   for (const owner of ['course','personal','debug']) {
    candidatesByOwner.set(owner, (candidatesByOwner.get(owner) ?? []).filter(n => !cancelled.has(n.extra?.kind)))
   }
   return enqueue(async () => {
    const plugin = (await getPluginModule())?.LocalNotifications
    if (!plugin) return 0
    const notifications = ((await plugin.getPending())?.notifications ?? []).filter(n => owned(n) && cancelled.has(n.extra.kind))
    if (notifications.length) await plugin.cancel({ notifications: notifications.map(n => ({ id: Number(n.id) })) })
    return notifications.length
   })
  },
 }
}
export const localNotificationCoordinator = createLocalNotificationCoordinator()
